import { isValid, parse, parseISO } from "date-fns";
import { getCurrentMonthPayedTillValue } from "../components/form/fields/inputFields";

const dateOptions = { year: "numeric", month: "2-digit", day: "2-digit" };

export const getDateFromDateField = (date) => {
  if (date == null) {
    return null;
  }
  if (typeof date === "object" && Object.hasOwn(date, "$d")) {
    return date.$d;
  }

  return date;
};

export const displayDate = (date) => {
  let d = new Date(date).toLocaleDateString("de-DE", dateOptions);
  if (d === "Invalid Date") {
    d = "Kein Datum";
  }
  return d;
};

export function formatIBAN(input) {
  // Convert input to a string
  const inputString = String(input);

  // Remove non-alphanumeric characters
  const cleanedInput = inputString.replace(/[^a-zA-Z0-9]/g, "");

  // Extract the first two letters and the next 20 characters (both letters and numbers)
  const firstTwoLetters = cleanedInput.slice(0, 2).toUpperCase();
  const remainingCharacters = cleanedInput.slice(2, 27);

  // Ensure that the first two characters are letters
  const lettersOnly = firstTwoLetters.replace(/[^a-zA-Z]/g, "");

  // Ensure that the remaining characters are numbers
  const numbersOnly = remainingCharacters.replace(/[^0-9]/g, "");

  // Format the whole input by adding a space after every four characters
  const formattedInput = (lettersOnly + numbersOnly).replace(/(.{4})/g, "$1 ");

  // Combine the letters and formatted numbers
  const result = `${formattedInput}`.trim();

  // Ensure the total length is not more than 27 characters
  return result.slice(0, 27);
}

export const getDateFromDateString = (dateString) => {
  const now = new Date();
  if (dateString == null || dateString === "") return now;

  // Already a Date
  if (dateString instanceof Date)
    return isValid(dateString) ? new Date(dateString) : now;

  // Timestamp (ms)
  if (typeof dateString === "number" && Number.isFinite(dateString)) {
    const d = new Date(dateString);
    return isValid(d) ? d : now;
  }

  const s = String(dateString).trim();
  if (!s) return now;

  // "YYYY-MM-DD" (treat as local date to avoid timezone shifts)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map((part) => parseInt(part, 10));
    if (
      Number.isFinite(y) &&
      Number.isFinite(m) &&
      Number.isFinite(d) &&
      m >= 1 &&
      m <= 12 &&
      d >= 1 &&
      d <= 31
    ) {
      return new Date(y, m - 1, d);
    }
  }

  // "DD.MM.YYYY" (allow extra text after, e.g. time)
  const first10 = s.slice(0, 10);
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(first10)) {
    const d = parse(first10, "dd.MM.yyyy", now);
    return isValid(d) ? d : now;
  }

  // Numeric string timestamp (ms)
  if (/^\d+$/.test(s)) {
    const d = new Date(Number(s));
    return isValid(d) ? d : now;
  }

  // ISO 8601 (e.g., "2025-07-27T13:19:00.000Z" or "2025-07-27")
  try {
    const d = parseISO(s);
    if (isValid(d)) return d;
  } catch {
    // ignore and fall through
  }

  // Fallback: native Date parsing
  const d = new Date(s);
  return isValid(d) ? d : now;
};

const handleCheckDegree = (degree) => {
  if (!isNaN(degree)) {
    return parseInt(degree - 1, 10);
  }

  const degreeLowerCase = degree.toLowerCase(); // Um Groß- und Kleinschreibung zu ignorieren

  if (degreeLowerCase.includes("kup")) {
    const kupNumber = parseInt(degreeLowerCase.replace("kup", "").trim(), 10);
    return isNaN(kupNumber) ? 0 : 10 - kupNumber;
  } else if (degreeLowerCase.includes("dan")) {
    const danNumber = parseInt(degreeLowerCase.replace("dan", "").trim(), 10);
    return isNaN(danNumber) ? 0 : danNumber;
  } else if (degreeLowerCase.includes("poom")) {
    return 10; // Poom entspricht Wert 10
  }

  return 0; // Gültiger Gürtelgrad nicht erkannt
};

const handleIsChild = (text) => {
  let isChild = false;
  if (text.toLowerCase().includes("kind")) {
    isChild = true;
  }

  return isChild;
};

const handleStatus = (text) => {
  let status = "active";

  if (text.toLowerCase().includes("kündig")) {
    status = "terminated";
  }

  if (text.toLowerCase().includes("termin")) {
    status = "terminated";
  }

  if (text.toLowerCase().includes("gefror")) {
    status = "frozen";
  }

  if (text.toLowerCase().includes("froz")) {
    status = "frozen";
  }

  return status;
};

const toNumberOrNull = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).replace(",", ".").trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const checkDataBelongsToContract = (value) => {
  return typeof value === "string" && value.startsWith("contract-");
};

export const assignImportedDataToMemberObjects = (
  importedData,
  assignedFields,
) => {
  // Iterate through the arrays of the imported data
  let assignedMembers = importedData.map((importedMember, index) => {
    let member = {};
    let contract = {};
    // Iterate through each header and assign value
    for (const [key, value] of Object.entries(assignedFields)) {
      let i = importedData[0].indexOf(key);

      // Handle Contract Data
      if (checkDataBelongsToContract(value)) {
        let k = value.replace("contract-", "");
        // Check date and convert:
        if (k === "birthdate") {
          contract[k] = getDateFromDateString(importedMember[i]);
          continue;
        }
        if (
          k === "contractDate" ||
          k === "terminationDate" ||
          k === "beginningDate" ||
          k === "lastTesting"
        ) {
          contract[k] = getDateFromDateString(importedMember[i]);
          continue;
        }
        if (["contractDuration", "contractExtension", "noticePeriod"].includes(k)) {
          contract[k] = toNumberOrNull(importedMember[i]);
          continue;
        }
        if (k === "iban") {
          contract[k] = formatIBAN(importedMember[i] || "");
          continue;
        }
        contract[k] = importedMember[i];
        continue;
      }

      // Check Degree
      if (key === "Gürtelgrad") {
        // Handle convertion to Degree Index
        member[value] = handleCheckDegree(importedMember[i]);
        continue;
      }

      // Check status
      if (key === "Status") {
        member[value] = handleStatus(importedMember[i]);
        continue;
      }
      // Check termination
      if (key === "Mitgliedsart") {
        let isChild = handleIsChild(importedMember[i]);
        member[value] = isChild ? "child" : "adult";
        member.isChild = isChild;
        continue;
      }

      // Check date and convert:
      if (
        value.substring(value.length - 4).toLowerCase() === "date" ||
        key === "Letzte Prüfung" ||
        key === "Aktiv seit" ||
        key === "Vertragsdatum" ||
        key === "Kündigungsdatum" ||
        key === "Geburtsdatum"
      ) {
        member[value] = getDateFromDateString(importedMember[i]);
        continue;
      }
      if (
        [
          "contractDuration",
          "contractExtension",
          "noticePeriod",
          "contribution",
          "registrationFee",
        ].includes(value)
      ) {
        member[value] = toNumberOrNull(importedMember[i]);
        continue;
      }
      member[value] = importedMember[i];
    }
    member.payedTill = getCurrentMonthPayedTillValue();
    member.importID = `import-${index}`;
    member.contractData = contract;
    return member;
  });
  // Remove first array which contains only headers
  assignedMembers.shift();
  return assignedMembers;
};

export function getMembersWithSameLastNameAndIban(members) {
  const groups = {}; // key: "<lastName>|<iban>"
  const pairedMembers = [];
  const uniqueLastNames = []; // keeping your original return field name (these are uniques by lastname+iban)

  members.forEach((member) => {
    const lastName = (member?.lastName ?? "").trim();
    const iban = (member?.contractData?.iban ?? "").trim();

    // If either is missing, it can't be paired reliably -> treat as unique
    if (!lastName || !iban) {
      uniqueLastNames.push(member);
      return;
    }

    const key = `${lastName.toLowerCase()}|${iban
      .replace(/\s+/g, "")
      .toLowerCase()}`;

    if (!groups[key]) groups[key] = [];
    groups[key].push(member);
  });

  for (const key in groups) {
    if (groups[key].length > 1) {
      pairedMembers.push(groups[key]);
    } else {
      uniqueLastNames.push(groups[key][0]);
    }
  }

  return { pairedMembers, uniqueLastNames };
}

export const getMemberCountPerSite = (members) => {
  // Use reduce to create a summary object
  const summary = members.reduce((acc, member) => {
    // Extract the site name from the member
    const siteName = member?.standortRef?.siteName;

    // Check if the site name is already in the summary
    if (acc.hasOwnProperty(siteName)) {
      // Increment the member count for the existing site
      acc[siteName].count += 1;
    } else {
      // Add a new entry for the site with a member count of 1
      acc[siteName] = { siteName, count: 1 };
    }

    return acc;
  }, {});

  // Convert the summary object to an array
  const summaryArray = Object.values(summary);

  return summaryArray;
};
